/*
 * This file contains the utilities for navigating to a link from a confluence result card.
 * We mainly want to support SPA navigation if search-page provides its own onNavigate function
 * inspired by
 *  - /platform/packages/search/search-page/src/common/ui/link-component/index.tsx
 *  - /platform/packages/search/search-page/src/common/utils/url.ts
 */

import type { KeyboardEvent, MouseEvent } from 'react';

import { logger } from '../../../../../../common/utils/logger';

export type NavigateCallbackEvent = { preventDefault: VoidFunction };

export type NavigateCallback = (
	url: string,
	action: 'push' | 'replace',
	event?: NavigateCallbackEvent,
) => void;

/**
 * Allows customisation of how the application navigates to a new URL, e.g. using router functions
 * the function is passed in from the search-page packageas a prop
 */
export type OnNavigate =
	| NavigateCallback
	| {
			/**
			 * Function that is used when changing the browser's URL. If not provided,
			 * the default will be an assignment to window.location.href
			 */
			callback: NavigateCallback;

			options: {
				/**
				 * The default behavior for link clicks is to use the onNavigate function
				 * only when the link is clicked without a modifier (ctrl, cmd, shift)
				 * and when the href is on the same origin.
				 *
				 * If this option is enabled, the onNavigate function will always be used
				 * when a link is clicked.
				 */
				alwaysUseOnLinkClick: boolean;
			};
	  };

const defaultOnNavigate = {
	callback: (url: string) => {
		window.location.href = url;
	},
	options: {
		alwaysUseOnLinkClick: false,
	},
};

export const navigateToLink = ({
	event,
	url,
	onNavigate = defaultOnNavigate,
}: {
	event: MouseEvent | KeyboardEvent;
	url: string;
	onNavigate?: OnNavigate;
}): void => {
	try {
		const metaPressed = event.ctrlKey || event.metaKey || event.shiftKey;
		const sameOrigin = window.location.origin === new URL(url).origin;

		const useOnNavigate = sameOrigin && !metaPressed;
		if (useOnNavigate) {
			event.preventDefault();

			// Handles SPA navigation if search-page provides own onNavigate
			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;
			onNavigateCallback(url, 'push');
		} else {
			defaultOnNavigate.callback(url);
		}
	} catch (error) {
		logger.error({ error, functionName: 'navigateToLink' });
		defaultOnNavigate.callback(url);
	}
};
